<template>
  <div class="rubric-view-table">
    <rubrics-table
      class="w-100"
      :rubrics="filteredRubrics"
      :fields="fields"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :table-loading="tableLoading"
      :initial-loading="initialLoading"
      :total="filteredRubrics.length"
      :filters="filters"
      :is-filtered="isFiltered"
      :selected-all="selectedAll"
      :show-delete="actionsSettings.showDelete"
      :show-share="actionsSettings.showShare"
      :show-edit="actionsSettings.showEdit"
      @getData="fetchPlatformRubrics"
      @delete="deleteRubrics"
      @share="shareRubric"
      @edit="editRubric"
      @selectedAll="selectedAll = $event"
    />
  </div>
</template>

<script>
import RubricsTable from "@/components/rubrics/rubrics_table.vue";
import RubricShareModal from "@/components/rubrics/rubric_share_modal.vue";
import {
  fetchRubrics,
  filterRubrics,
  sortRubrics,
  deleteRubrics,
} from "@/components/rubrics/utils";
import { LOCAL_STORAGE_NEW_RUBRIC_ID } from "@/components/rubrics/constants";

export default {
  name: "RubricViewTable",
  components: {
    RubricsTable,
  },

  props: {
    rubricType: {
      type: String,
      required: true,
      default: "platform",
      validator: (value) => {
        return ["platform", "my", "shared"].includes(value);
      },
    },
  },

  data() {
    return {
      sortBy: "name",
      sortDesc: false,
      filters: {},
      selectedAll: false,
      rubrics: [],
      initialLoading: true,
      tableLoading: false,
    };
  },

  computed: {
    baseFields() {
      return [
        {
          key: "name",
          label: this.$t("rubrics.nameField"),
          tdClass: "text-truncate",
          columnClass: "col-name",
          egSortable: true,
        },
        {
          key: "gradeLevel",
          label: this.$t("rubrics.gradeField"),
          columnClass: "col-grade-level",
          egSortable: true,
        },
        {
          key: "intensity",
          label: this.$t("rubrics.intensityField"),
          columnClass: "col-intensity",
          egSortable: true,
        },
        {
          key: "createdAt",
          label: this.$t("rubrics.createdDateField"),
          columnClass: "col-created-date",
          egSortable: true,
        },
        {
          key: "actions",
          label: this.$t("rubrics.actionsField"),
          tdClass: "text-right",
          columnClass: "col-actions",
        },
      ];
    },

    fields() {
      if (this.rubricType !== "shared") {
        return this.baseFields;
      }

      // Add createdBy field for shared rubrics
      const fields = [...this.baseFields];
      const createdAtIndex = fields.findIndex((f) => f.key === "createdAt");
      fields.splice(createdAtIndex, 0, {
        key: "createdBy",
        label: this.$t("rubrics.createdByField"),
        columnClass: "col-created-by",
        egSortable: true,
      });

      return fields;
    },

    // Platform rubrics computed
    filteredRubrics() {
      const filtered = filterRubrics(this.rubrics, this.filters);
      return sortRubrics(filtered, this.sortBy, this.sortDesc);
    },

    isFiltered() {
      return Object.values(this.filters).some((value) => Boolean(value));
    },

    actionsSettings() {
      let actionsSettings = {
        showShare: false,
        showEdit: false,
        showDelete: false,
      };

      switch (this.rubricType) {
        case "my":
          actionsSettings.showShare = true;
          actionsSettings.showEdit = true;
          actionsSettings.showDelete = true;
          break;
        case "shared":
          actionsSettings.showShare = false;
          actionsSettings.showEdit = false;
          actionsSettings.showDelete = false;
          break;
        default:
          actionsSettings.showShare = false;
          actionsSettings.showEdit = false;
          actionsSettings.showDelete = false;
          break;
      }

      return actionsSettings;
    },
  },

  created() {
    this.init();

    if (this.rubricType !== "my") {
      return;
    }

    this.$eventBus.$on("rubric-created", this.rubricCreatedHandler);
  },

  beforeDestroy() {
    this.$eventBus.$off("rubric-created", this.rubricCreatedHandler);
  },

  methods: {
    async rubricCreatedHandler(eventData) {
      // Handle both old format (direct rubric) and new format ({ rubric, isCreating })
      const isCreating =
        eventData?.isCreating !== undefined ? eventData.isCreating : true;

      // Only refresh rubrics if this is a creation event
      if (isCreating) {
        await this.init();
      }
    },

    async init() {
      this.initialLoading = true;
      try {
        this.rubrics = await fetchRubrics(this.rubricType);
        this.$emit("input", this.rubrics);
        this.showNewRubricIfNeeded();
      } catch (error) {
        this.$showToastError(
          this.$t("rubrics.errorLoadingRubrics", { type: this.rubricType })
        );
        console.error(`Error loading ${this.rubricType} rubrics:`, error);
      } finally {
        this.initialLoading = false;
      }
    },

    async showNewRubricIfNeeded() {
      if (this.rubricType !== "my") {
        return;
      }

      const newRubricId = localStorage.getItem(LOCAL_STORAGE_NEW_RUBRIC_ID);

      if (!newRubricId) {
        return;
      }

      await this.$nextTick();
      const element = this.$el.querySelector(`[data-pk="${newRubricId}"]`);

      if (!element) {
        return;
      }

      localStorage.removeItem(LOCAL_STORAGE_NEW_RUBRIC_ID);
      element.focus();
      element.scrollIntoViewIfNeeded({ behavior: "smooth" });
    },

    fetchPlatformRubrics({ filters, sortBy, sortDesc } = {}) {
      if (filters) {
        this.filters = Object.entries(filters).reduce((acc, [key, value]) => {
          if (value) acc[key] = value;
          return acc;
        }, {});
      }
      if (sortBy !== undefined) {
        this.sortBy = sortBy;
      }
      if (sortDesc !== undefined) {
        this.sortDesc = sortDesc;
      }
    },

    async deleteRubrics(ids) {
      try {
        await deleteRubrics(ids);

        this.$showToastSuccess(this.$t("rubrics.deleteSuccess"), {
          title: this.$t("rubrics.deleteSuccessTitle"),
          autoHideDelay: 3000,
        });

        await this.init();
      } catch (error) {
        this.$showToastError(this.$t("rubrics.rubricDeleteError"));
        console.error("Error deleting rubric:", error);
      }
    },

    shareRubric(rubric) {
      this.$egModal.show("rubric-share-modal", {
        component: RubricShareModal,
        props: {
          rubric: rubric,
          zIndex: 1060,
          showBackdrop: true,
        },
      });
    },

    editRubric(rubric) {
      this.$router.push({
        name: "rubric-edit",
        params: { id: rubric.id },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.rubric-view-table {
  width: 100%;
  height: 100%;
  padding: 24px;

  .rubrics-table {
    border-radius: var(--radius-rd-400, 12px);
    border: 1px solid var(--color-stroke-neutral-default);
  }

  &::v-deep {
    .eg-table-base-filters {
      border-top-left-radius: var(--radius-rd-400, 12px);
      border-top-right-radius: var(--radius-rd-400, 12px);
    }
  }
}
</style>
